import React, { Suspense } from 'react';
import { AnimatePresence, motion as Motion, useReducedMotion } from 'framer-motion';
import { ArrowUpRight, Check, LoaderCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { MOTION_TOKENS } from '../lib/motionTokens';

const ProfileView = React.lazy(() => import('../features/ProfileView'));

const ProfileSkeleton = () => (
  <div className="premium-profile-skeleton" aria-label="Loading public profile">
    <div className="premium-profile-skeleton-avatar" />
    <div className="premium-profile-skeleton-line premium-profile-skeleton-line-title" />
    <div className="premium-profile-skeleton-line premium-profile-skeleton-line-copy" />
    <div className="premium-profile-skeleton-links">
      <div />
      <div />
      <div />
    </div>
  </div>
);

const PremiumProfileReveal = ({
  username,
  availability,
  loading,
  profile,
  links,
}) => {
  const reduceMotion = useReducedMotion();
  const normalizedUsername = String(username || '').trim().toLowerCase();
  const canCheck = normalizedUsername.length >= 3;
  const existingProfile = availability === 'taken' && profile;
  const available = availability === 'available' && canCheck;
  const checking = availability === 'verifying' || loading;
  const displayName = String(profile?.full_name || profile?.display_name || '').trim();

  return (
    <div className="premium-profile-reveal" aria-live="polite">
      <AnimatePresence mode="wait">
        {checking && canCheck && (
          <Motion.div
            key="checking"
            className="premium-profile-availability premium-profile-availability-checking"
            initial={reduceMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={MOTION_TOKENS.feedback}
          >
            <LoaderCircle size={17} className="animate-spin" />
            Looking up @{normalizedUsername}
          </Motion.div>
        )}

        {available && (
          <Motion.div
            key="available"
            className="premium-profile-availability premium-profile-availability-ready"
            initial={reduceMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={MOTION_TOKENS.component}
          >
            <span className="premium-profile-availability-icon"><Check size={16} /></span>
            <div>
              <strong>@{normalizedUsername} is ready to claim</strong>
              <span>Create the page and shape it around your work.</span>
            </div>
          </Motion.div>
        )}

        {existingProfile && (
          <Motion.section
            key={`profile-${profile.id || normalizedUsername}`}
            className="premium-profile-stage"
            aria-label={`Real Linkit profile for ${profile.full_name || normalizedUsername}`}
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={MOTION_TOKENS.reveal}
          >
            <div
              className="premium-profile-stage-copy"
            >
              <span>Live on Linkit</span>
              <h2>
                Meet {displayName || `@${normalizedUsername}`}, already live.
              </h2>
              <p>
                Explore a real published page and see how identity, destinations, products, and public content come together in one place.
              </p>
              <div>
                <Link to={`/${normalizedUsername}`} className="premium-profile-open">
                  Explore this profile <ArrowUpRight size={17} />
                </Link>
              </div>
            </div>

            <div className="premium-profile-device" aria-hidden="true">
              <div className="premium-profile-device-speaker" />
              <div className="premium-profile-device-screen">
                <Suspense fallback={<ProfileSkeleton />}>
                  <div className="premium-profile-render">
                    <ProfileView injectedProfile={profile} injectedLinks={links} isMockup />
                  </div>
                </Suspense>
              </div>
            </div>
          </Motion.section>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PremiumProfileReveal;
